import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Box,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Backdrop,
  Fade,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormGroup,
  FormControlLabel,
  Checkbox
} from '@mui/material';
import {
  Button
} from "@material-ui/core";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  School as SchoolIcon,
  Book as BookIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Close as CloseIcon,
  Search as SearchIcon,
  Class as ClassIcon,
  Person as PersonIcon,
  Numbers as NumbersIcon
} from '@mui/icons-material';
import MatiereService from '../../services/MatiereService';
import ClasseService from '../../services/ClasseService';
import MainContainer from '../../components/MainContainer';
import { headerBanqueCV } from '../../data/modules/banque-cv-header';


const Matieres = () => {
  const [matieres, setMatieres] = useState([]);
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [classFilter, setClassFilter] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editingMatiere, setEditingMatiere] = useState(null);
  const [formData, setFormData] = useState({ 
    nom: '', 
    encadreur: '', 
    ordre: 0, 
    classe_ids: [] 
  });
  const [formErrors, setFormErrors] = useState({ nom: false });
  const [classModalOpen, setClassModalOpen] = useState(false);
  const [selectedMatiere, setSelectedMatiere] = useState(null);
  const [feedbackDialog, setFeedbackDialog] = useState({
    open: false,
    type: 'success',
    title: '',
    message: '',
    onConfirm: null
  });
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: '',
    message: '',
    onConfirm: null
  });
  const [saveLoading, setSaveLoading] = useState(false);

  useEffect(() => {
    loadMatieres();
    loadClasses();
  }, []);

  const loadMatieres = async () => {
    setLoading(true);
    try {
      const result = await MatiereService.getAllMatieres();
      if (result && result.status === true) {
        setMatieres(Array.isArray(result.data) ? result.data : []);
      } else {
        setMatieres([]);
      }
    } catch (error) {
      console.error('Erreur lors du chargement des matières:', error);
      setMatieres([]);
    } finally {
      setLoading(false);
    }
  };

  const loadClasses = async () => {
    try {
      const result = await ClasseService.getAllClasses();
      if (result && result.status === true) {
        setClasses(Array.isArray(result.data) ? result.data : []);
      } else {
        setClasses([]);
      }
    } catch (error) {
      console.error('Erreur lors du chargement des classes:', error);
      setClasses([]);
    }
  };

  const showFeedback = (type, title, message, onConfirm = null) => {
    setFeedbackDialog({
      open: true,
      type,
      title,
      message,
      onConfirm
    });
  };

  const closeFeedback = () => {
    setFeedbackDialog({ ...feedbackDialog, open: false });
    if (feedbackDialog.onConfirm) {
      feedbackDialog.onConfirm();
    }
  };

  const validateForm = () => {
    const errors = { nom: !formData.nom.trim() };
    setFormErrors(errors);
    return !errors.nom;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      showFeedback('error', 'Erreur de validation', 'Le nom de la matière est requis');
      return;
    }

    setSaveLoading(true);
    try {
      let result;
      if (editingMatiere) {
        result = await MatiereService.updateMatiere(editingMatiere.id, formData);
      } else {
        result = await MatiereService.createMatiere(formData);
      }

      if (result && result.status === true) {
        showFeedback('success', 'Succès', result.message || 'Opération réussie', () => {
          setModalOpen(false);
          setEditingMatiere(null);
          setFormData({ nom: '', encadreur: '', ordre: 0, classe_ids: [] });
          setFormErrors({ nom: false });
          loadMatieres();
        });
      } else {
        showFeedback('error', 'Erreur', result?.message || 'Erreur lors de l\'enregistrement');
      }
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
      showFeedback('error', 'Erreur', error?.message || 'Erreur lors de l\'enregistrement');
    } finally {
      setSaveLoading(false);
    }
  };

  const handleDelete = async (id, nom) => {
    setConfirmDialog({
      open: true,
      title: 'Confirmation de suppression',
      message: `Êtes-vous sûr de vouloir supprimer la matière "${nom}" ? Tous les cours associés seront également supprimés. Cette action est irréversible.`,
      onConfirm: async () => {
        setConfirmDialog({ ...confirmDialog, open: false });
        setSaveLoading(true);
        try {
          const result = await MatiereService.deleteMatiere(id);
          if (result && result.status === true) {
            showFeedback('success', 'Succès', result.message || 'Matière supprimée avec succès', () => {
              loadMatieres();
            });
          } else {
            showFeedback('error', 'Erreur', result?.message || 'Erreur lors de la suppression');
          }
        } catch (error) {
          console.error('Erreur lors de la suppression:', error);
          showFeedback('error', 'Erreur', error?.message || 'Erreur lors de la suppression');
        } finally {
          setSaveLoading(false);
        }
      }
    });
  };

  const openClassManager = async (matiere) => {
    setSelectedMatiere(matiere);
    try {
      const result = await MatiereService.getMatiereClasses(matiere.id);
      if (result && result.status === true) {
        setFormData(prev => ({
          ...prev,
          classe_ids: Array.isArray(result.data) ? result.data.map(c => c.id) : []
        }));
      }
      setClassModalOpen(true);
    } catch (error) {
      console.error('Erreur lors du chargement des classes:', error);
      showFeedback('error', 'Erreur', 'Erreur lors du chargement des classes associées');
    }
  };

  const toggleClasseInMatiere = (classeId) => {
    setFormData(prev => ({
      ...prev,
      classe_ids: prev.classe_ids.includes(classeId)
        ? prev.classe_ids.filter(id => id !== classeId)
        : [...prev.classe_ids, classeId]
    }));
  };

  const saveMatiereClasses = async () => {
    if (!selectedMatiere) return;
    
    setSaveLoading(true);
    try {
      const result = await MatiereService.syncMatiereClasses(selectedMatiere.id, formData.classe_ids);
      if (result && result.status === true) {
        showFeedback('success', 'Succès', result.message || 'Classes synchronisées avec succès', () => {
          setClassModalOpen(false);
          loadMatieres();
        });
      } else {
        showFeedback('error', 'Erreur', result?.message || 'Erreur lors de la synchronisation');
      }
    } catch (error) {
      console.error('Erreur lors de la synchronisation:', error);
      showFeedback('error', 'Erreur', error?.message || 'Erreur lors de la synchronisation');
    } finally {
      setSaveLoading(false);
    }
  };

  const openModalForEdit = (matiere) => {
    if (matiere) {
      setEditingMatiere(matiere);
      setFormData({
        nom: matiere.nom || '',
        encadreur: matiere.encadreur || '',
        ordre: matiere.ordre || 0,
        classe_ids: (matiere.classes || []).map(c => c.id)
      });
      setFormErrors({ nom: false });
      setModalOpen(true);
    }
  };

  const openModalForCreate = () => {
    setEditingMatiere(null);
    setFormData({ nom: '', encadreur: '', ordre: 0, classe_ids: [] });
    setFormErrors({ nom: false });
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    if (!saveLoading) {
      setModalOpen(false);
      setEditingMatiere(null);
      setFormData({ nom: '', encadreur: '', ordre: 0, classe_ids: [] });
      setFormErrors({ nom: false });
    }
  };

  const handleCloseClassModal = () => {
    if (!saveLoading) {
      setClassModalOpen(false);
      setSelectedMatiere(null);
    }
  };

  // Filtrer les matières par NOM de classe
  const filteredMatieres = Array.isArray(matieres) 
    ? matieres.filter(m => {
        const matchSearch = m.nom.toLowerCase().includes(searchTerm.toLowerCase());
        
        // Filtre par classe en utilisant le NOM
        let matchClass = true;
        if (classFilter) {
          // Vérifier si la matière a des classes associées
          if (m.classes && Array.isArray(m.classes) && m.classes.length > 0) {
            // Vérifier si le NOM de la classe filtrée existe dans les classes de la matière
            matchClass = m.classes.some(c => c.nom === classFilter);
          } else {
            matchClass = false;
          }
        }
        
        return matchSearch && matchClass;
      })
    : [];

  // Obtenir l'icône et la couleur en fonction du type de feedback
  const getFeedbackIcon = (type) => {
    switch (type) {
      case 'success':
        return <CheckCircleIcon sx={{ fontSize: 60, color: '#4caf50' }} />;
      case 'error':
        return <ErrorIcon sx={{ fontSize: 60, color: '#f44336' }} />;
      case 'warning':
        return <WarningIcon sx={{ fontSize: 60, color: '#ff9800' }} />;
      default:
        return <BookIcon sx={{ fontSize: 60, color: '#2196f3' }} />;
    }
  };

  const getFeedbackColor = (type) => {
    switch (type) {
      case 'success':
        return '#4caf50';
      case 'error':
        return '#f44336';
      case 'warning':
        return '#ff9800';
      default:
        return '#2196f3';
    }
  };
    
  return (
    <MainContainer {...headerBanqueCV}>
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <BookIcon sx={{ fontSize: 32, color: 'primary.main' }} />
            <Typography variant="h4" component="h1">
              Gestion des Matières
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={openModalForCreate}
            disabled={saveLoading}
          >
            Nouvelle matière
          </Button>
        </Box>

        {/* Filtres */}
        <Box sx={{ mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <TextField
            sx={{ flex: 1, minWidth: 200 }}
            variant="outlined"
            placeholder="Rechercher une matière..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
              endAdornment: searchTerm && (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => setSearchTerm('')}
                    edge="end"
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              )
            }}
          />
          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel>Filtrer par classe</InputLabel>
            <Select
              value={classFilter}
              onChange={(e) => setClassFilter(e.target.value)}
              label="Filtrer par classe"
            >
              <MenuItem value="">Toutes les classes</MenuItem>
              {classes.map(c => (
                <MenuItem key={c.id} value={c.nom}>{c.nom}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {/* Description recherche */}
        <Typography 
          variant="caption" 
          color="textSecondary" 
          sx={{ mb: 2, display: 'block', ml: 1 }}
        >
          <SearchIcon sx={{ fontSize: 12, mr: 0.5, verticalAlign: 'middle' }} />
          Veuillez taper le nom de la matière que vous recherchez
        </Typography>

        {/* Statistics */}
        <Box sx={{ mb: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Chip 
            label={`Total: ${filteredMatieres.length}`} 
            color="primary" 
            variant="outlined"
          />
          {searchTerm && (
            <Chip 
              label={`Résultats pour: "${searchTerm}"`} 
              color="secondary" 
              variant="outlined"
              onDelete={() => setSearchTerm('')}
            />
          )}
          {classFilter && (
            <Chip 
              label={`Classe: ${classFilter}`} 
              color="info" 
              variant="outlined"
              onDelete={() => setClassFilter('')}
            />
          )}
        </Box>

        {/* Table */}
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: 'primary.main' }}>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>N°</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Matière</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Encadreur</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Classes</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Nb Cours</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Ordre</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 5 }}>
                    <CircularProgress />
                    <Typography sx={{ mt: 1 }}>Chargement...</Typography>
                  </TableCell>
                </TableRow>
              ) : filteredMatieres.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 5 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                      {searchTerm || classFilter ? (
                        <>
                          <SearchIcon sx={{ fontSize: 48, color: 'text.secondary' }} />
                          <Typography color="textSecondary">
                            Aucune matière ne correspond aux critères
                          </Typography>
                          <Button onClick={() => {
                            setSearchTerm('');
                            setClassFilter('');
                          }}>
                            Effacer les filtres
                          </Button>
                        </>
                      ) : (
                        <Typography color="textSecondary">
                          Aucune matière trouvée. Cliquez sur "Nouvelle matière" pour en créer une.
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                filteredMatieres.map((matiere, index) => (
                  <TableRow key={matiere.id || index} hover>
                    <TableCell>{index + 1}</TableCell>
                    <TableCell>
                      <Typography variant="body1" fontWeight="bold">
                        {matiere.nom || 'Sans nom'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <PersonIcon fontSize="small" color="action" />
                        {matiere.encadreur || '-'}
                      </Box>
                    </TableCell>
                    <TableCell>
                      {matiere.classes && matiere.classes.length > 0 ? (
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                          {matiere.classes.map(c => (
                            <Chip
                              key={c.id}
                              label={c.nom}
                              size="small"
                              color="primary"
                              variant="outlined"
                              icon={<ClassIcon />}
                            />
                          ))}
                        </Box>
                      ) : '-'}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={matiere.cours_count || 0}
                        size="small"
                        color="info"
                        variant="filled"
                      />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <NumbersIcon fontSize="small" color="action" />
                        {matiere.ordre || 0}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <IconButton
                        color="primary"
                        onClick={() => openClassManager(matiere)}
                        size="small"
                        disabled={saveLoading}
                        title="Gérer les classes"
                      >
                        <ClassIcon />
                      </IconButton>
                      <IconButton
                        color="primary"
                        onClick={() => openModalForEdit(matiere)}
                        size="small"
                        disabled={saveLoading}
                        title="Modifier"
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        color="error"
                        onClick={() => handleDelete(matiere.id, matiere.nom)}
                        size="small"
                        disabled={saveLoading}
                        title="Supprimer"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Modal Matière */}
        <Dialog 
          open={modalOpen} 
          onClose={handleCloseModal} 
          maxWidth="sm" 
          fullWidth
          disableEscapeKeyDown={saveLoading}
        >
          <DialogTitle>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <BookIcon color="primary" />
              <Typography variant="h6">
                {editingMatiere ? 'Modifier la matière' : 'Nouvelle matière'}
              </Typography>
            </Box>
          </DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <TextField
                label="Nom de la matière *"
                value={formData.nom}
                onChange={(e) => {
                  setFormData({ ...formData, nom: e.target.value });
                  if (formErrors.nom && e.target.value.trim()) {
                    setFormErrors({ ...formErrors, nom: false });
                  }
                }}
                placeholder="Ex: Programmation Web"
                fullWidth
                required
                error={formErrors.nom}
                helperText={formErrors.nom && "Le nom de la matière est requis"}
                disabled={saveLoading}
                autoFocus
              />
              <TextField
                label="Encadreur"
                value={formData.encadreur}
                onChange={(e) => setFormData({ ...formData, encadreur: e.target.value })}
                placeholder="Nom de l'encadreur"
                fullWidth
                disabled={saveLoading}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PersonIcon color="action" />
                    </InputAdornment>
                  )
                }}
              />
              <TextField
                label="Ordre d'affichage"
                type="number"
                value={formData.ordre}
                onChange={(e) => setFormData({ ...formData, ordre: parseInt(e.target.value) || 0 })}
                min="0"
                fullWidth
                disabled={saveLoading}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <NumbersIcon color="action" />
                    </InputAdornment>
                  )
                }}
                helperText="Plus le nombre est petit, plus la matière apparaît en haut"
              />
              <Typography variant="subtitle2" sx={{ mt: 1 }}>
                Classes associées
              </Typography>
              <Paper variant="outlined" sx={{ p: 2, maxHeight: 200, overflow: 'auto' }}>
                <FormGroup>
                  {classes.map(c => (
                    <FormControlLabel
                      key={c.id}
                      control={
                        <Checkbox
                          checked={formData.classe_ids.includes(c.id)}
                          onChange={() => {
                            setFormData(prev => ({
                              ...prev,
                              classe_ids: prev.classe_ids.includes(c.id)
                                ? prev.classe_ids.filter(id => id !== c.id)
                                : [...prev.classe_ids, c.id]
                            }));
                          }}
                          disabled={saveLoading}
                        />
                      }
                      label={c.nom}
                    />
                  ))}
                  {classes.length === 0 && (
                    <Typography color="textSecondary" align="center">
                      Aucune classe disponible
                    </Typography>
                  )}
                </FormGroup>
              </Paper>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button 
              onClick={handleCloseModal} 
              disabled={saveLoading}
            >
              Annuler
            </Button>
            <Button 
              onClick={handleSave} 
              variant="contained" 
              color="primary"
              disabled={saveLoading}
              startIcon={saveLoading ? <CircularProgress size={20} /> : null}
            >
              {saveLoading ? 'Enregistrement...' : 'Enregistrer'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Modal Gestion des classes pour matière */}
        <Dialog
          open={classModalOpen}
          onClose={handleCloseClassModal}
          maxWidth="sm"
          fullWidth
          disableEscapeKeyDown={saveLoading}
        >
          <DialogTitle>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ClassIcon color="primary" />
              <Typography variant="h6">
                Gérer les classes - {selectedMatiere?.nom || ''}
              </Typography>
            </Box>
          </DialogTitle>
          <DialogContent>
            <Box sx={{ mt: 1 }}>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                Sélectionnez les classes qui auront accès à cette matière
              </Typography>
              <Paper variant="outlined" sx={{ p: 2, maxHeight: 400, overflow: 'auto', mt: 2 }}>
                <FormGroup>
                  {classes.map(c => (
                    <FormControlLabel
                      key={c.id}
                      control={
                        <Checkbox
                          checked={formData.classe_ids.includes(c.id)}
                          onChange={() => toggleClasseInMatiere(c.id)}
                          disabled={saveLoading}
                        />
                      }
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <SchoolIcon fontSize="small" color="action" />
                          {c.nom}
                        </Box>
                      }
                    />
                  ))}
                  {classes.length === 0 && (
                    <Typography color="textSecondary" align="center">
                      Aucune classe disponible
                    </Typography>
                  )}
                </FormGroup>
              </Paper>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button 
              onClick={handleCloseClassModal} 
              disabled={saveLoading}
            >
              Annuler
            </Button>
            <Button 
              onClick={saveMatiereClasses} 
              variant="contained" 
              color="primary"
              disabled={saveLoading}
              startIcon={saveLoading ? <CircularProgress size={20} /> : null}
            >
              {saveLoading ? 'Synchronisation...' : 'Enregistrer'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Feedback Dialog */}
        <Dialog
          open={feedbackDialog.open}
          onClose={closeFeedback}
          maxWidth="xs"
          fullWidth
          TransitionComponent={Fade}
          transitionDuration={300}
        >
          <DialogTitle sx={{ textAlign: 'center', pb: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <IconButton onClick={closeFeedback} size="small">
                <CloseIcon />
              </IconButton>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
              {getFeedbackIcon(feedbackDialog.type)}
            </Box>
          </DialogTitle>
          <DialogContent sx={{ textAlign: 'center', pb: 3 }}>
            <Typography 
              variant="h6" 
              gutterBottom
              sx={{ color: getFeedbackColor(feedbackDialog.type), fontWeight: 'bold' }}
            >
              {feedbackDialog.title}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {feedbackDialog.message}
            </Typography>
          </DialogContent>
          <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
            <Button 
              onClick={closeFeedback}
              variant="contained"
              sx={{
                backgroundColor: getFeedbackColor(feedbackDialog.type),
                '&:hover': {
                  backgroundColor: getFeedbackColor(feedbackDialog.type),
                  opacity: 0.9
                }
              }}
            >
              OK
            </Button>
          </DialogActions>
        </Dialog>

        {/* Dialog de confirmation pour suppression */}
        <Dialog
          open={confirmDialog.open}
          onClose={() => !saveLoading && setConfirmDialog({ ...confirmDialog, open: false })}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <WarningIcon color="warning" />
            {confirmDialog.title}
          </DialogTitle>
          <DialogContent>
            <Typography>{confirmDialog.message}</Typography>
          </DialogContent>
          <DialogActions>
            <Button 
              onClick={() => setConfirmDialog({ ...confirmDialog, open: false })}
              disabled={saveLoading}
            >
              Annuler
            </Button>
            <Button 
              onClick={confirmDialog.onConfirm} 
              variant="contained" 
              color="error"
              disabled={saveLoading}
            >
              {saveLoading ? 'Suppression...' : 'Confirmer'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Backdrop pour loading */}
        <Backdrop
          sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
          open={saveLoading}
        >
          <CircularProgress color="inherit" />
        </Backdrop>
      </Container>
    </MainContainer>
  );
};

export default Matieres;